import { groupBy } from 'lodash';
import { useContext } from 'react';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';
import { type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { FontFamilySelector, type FontItem } from '@/components/font-family-selector';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { TextPanelContext } from '../../context';
import { useEditingRange, useLayerProps, useTextContent } from '../../hooks';
import { Tracker } from '../../utils';

type Props = {
    fonts: FontItem[];
    enableRemoteSearch?: boolean;
};

export const FontFamily: React.FC<Props> = ({ fonts, enableRemoteSearch = true }) => {
    const editor = useEditor();
    const { layerId, recordId, isVIP, onOpenVIPModal, isIntl } = useContext(TextPanelContext)!;

    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { fontFamily, advancedTextMaterial } = layerData;

        return {
            fontFamily,
            advancedTextMaterial,
        };
    });

    const fontFamily = props?.fontFamily;
    const advancedTextMaterial = props?.advancedTextMaterial;
    const ignoreRange = !!advancedTextMaterial;

    const editingRange = useEditingRange(editor);
    const textContent = useTextContent(editor, editingRange);

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const finalFontFamily = (() => {
        if (textContent) return textContent.fontFamily;
        return fontFamily ? fontFamily : undefined;
    })();

    const usingFonts = editor.font.getUsingList(editor.active.getActiveOriginIndex());

    const handleOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openFontFamilySelector({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    const handleChange = (data: FontItem, fontName: string, isFromSearch: boolean) => {
        editor.getInstance(TextLayerExtension).setFontFamily(layerId, fontName, { ignoreRange });

        Tracker.setFontFamily({
            recordId,
            stageType: editor.stage.getType(),
            font: data,
            isSearch: isFromSearch,
        });
    };

    return (
        <FontFamilySelector
            value={finalFontFamily}
            fonts={groupBy(fonts, 'category_name')}
            isVip={isVIP}
            onClickVipBtn={onOpenVIPModal}
            usingFonts={usingFonts}
            enableRemoteSearch={!isIntl && enableRemoteSearch}
            onChange={handleChange}
            onDropdownVisibleChange={handleOpenChange}
        />
    );
};
